import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// Parse a yyyy-mm-dd string as a LOCAL date (avoids UTC/timezone drift).
function parseISO(value?: string | null): Date | undefined {
  if (!value) return undefined;
  const [y, m, d] = value.split("-").map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
}

function toISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function sameDay(a?: Date, b?: Date): boolean {
  return (
    !!a && !!b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export interface CalendarProps {
  value?: string; // yyyy-mm-dd
  onSelect?: (value: string) => void;
  className?: string;
}

/** Self-contained month calendar (no external date library). Values are yyyy-mm-dd. */
function Calendar({ value, onSelect, className }: CalendarProps) {
  const selected = parseISO(value);
  const [view, setView] = React.useState<Date>(() => selected ?? new Date());

  const year = view.getFullYear();
  const month = view.getMonth();
  const startDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date();

  const cells: (Date | null)[] = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  return (
    <div className={cn("w-fit select-none", className)}>
      <div className="flex items-center justify-between pb-2">
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label="Previous month"
          onClick={() => setView(new Date(year, month - 1, 1))}
        >
          <ChevronLeft className="size-4" />
        </Button>
        <div className="text-sm font-medium">
          {MONTHS[month]} {year}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label="Next month"
          onClick={() => setView(new Date(year, month + 1, 1))}
        >
          <ChevronRight className="size-4" />
        </Button>
      </div>

      <div className="grid grid-cols-7 gap-0.5">
        {WEEKDAYS.map((w) => (
          <div
            key={w}
            className="text-muted-foreground flex h-8 items-center justify-center text-xs"
          >
            {w}
          </div>
        ))}
        {cells.map((d, i) =>
          d === null ? (
            <div key={`empty-${i}`} />
          ) : (
            <button
              key={toISO(d)}
              type="button"
              onClick={() => onSelect?.(toISO(d))}
              className={cn(
                "flex size-8 items-center justify-center rounded-md text-sm tabular-nums transition-colors",
                sameDay(d, selected)
                  ? "bg-primary text-primary-foreground"
                  : sameDay(d, today)
                    ? "bg-accent text-accent-foreground"
                    : "hover:bg-accent hover:text-accent-foreground"
              )}
            >
              {d.getDate()}
            </button>
          )
        )}
      </div>
    </div>
  );
}

export { Calendar };
